import { alpha, Typography } from '@mui/material'
import Button from '@mui/material/Button'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import { useQuery } from '@tanstack/react-query'
import theme from 'assets/theme'
import KNCallToAction from 'components/KNCallToAction/KNCallToAction'
import KNLoader from 'components/KNLoader/KNLoader'
import useDialog from 'global/hooks/useDialog'
import useSuppressFlag from 'global/hooks/useSuppressFlag'
import { type ReactElement, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { contactsQueryOptions } from './CarrierProfile.service'
import ContactsTable from './ContactsTable'
import CreateOrEditContactDialog from './CreateOrEditContactDialog'

const ContactsTab = (): ReactElement => {
  const { t } = useTranslation()
  const contactsQuery = useQuery(contactsQueryOptions)
  const contacts = contactsQuery.data ?? []
  const { suppressIndefinitely, isSuppressed } = useSuppressFlag('data-protection-disclaimer')
  const [disclaimerVisible, setDisclaimerVisible] = useState(!isSuppressed)

  const createContactDialog = useDialog<typeof CreateOrEditContactDialog>()

  if (contactsQuery.isPending) {
    return (
      <KNLoader />
    )
  }

  return (
    <>
      {disclaimerVisible && (
        <Paper
          sx={{
            padding: 2,
            marginBottom: 2,
            backgroundColor: alpha(theme.palette.primary.dark, 0.85),
            backdropFilter: 'blur(3px)',
            color: theme.palette.success.contrastText,
            maxWidth: '50%',
          }}
        >
          <Stack direction="column" spacing={2}>
            <Typography component="p" variant="p6">
              {t('screens.cs.carrier_profile.contacts.data_protection_notice.header')}
            </Typography>
            <Typography component="p" variant="p5">
              {t('screens.cs.carrier_profile.contacts.data_protection_notice.content')}
            </Typography>
            <Button
              variant="outlined"
              size="small"
              color="inherit"
              sx={{
                color: 'white !important',
                alignSelf: 'start',
              }}
              onClick={() => {
                setDisclaimerVisible(false)
                suppressIndefinitely()
              }}
            >
              {t('screens.cs.carrier_profile.contacts.data_protection_notice.confirmation')}
            </Button>
          </Stack>
        </Paper>
      )}
      {contacts.length > 0 ? (
        <>
          <Stack direction="row" spacing={1} mb={1}>
            <Button
              onClick={createContactDialog.handleOpen}
              variant="contained"
              color="secondary"
              size="small"
              sx={{
                alignSelf: 'start',
              }}
            >
              {t('screens.cs.carrier_profile.contacts.add_contact')}
            </Button>
          </Stack>
          <ContactsTable contacts={contacts} />
        </>
      ) : (
        <KNCallToAction
          header={t('screens.cs.carrier_profile.contacts.empty_header')}
          content={t('screens.cs.carrier_profile.contacts.empty_content')}
          sx={{ minHeight: '20rem' }}
        >
          <Button
            variant="contained"
            color="secondary"
            onClick={createContactDialog.handleOpen}
          >
            {t('screens.cs.carrier_profile.contacts.add_contact')}
          </Button>
        </KNCallToAction>
      )}
      <CreateOrEditContactDialog
        open={createContactDialog.isOpen}
        onAction={createContactDialog.handleAction}
        onClose={createContactDialog.handleClose}
      />
    </>
  )
}

export default ContactsTab
